import { Box, Button, Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import type { RelatedChangeSets, WorkflowActivityChangeSet } from "../../../../../api/types/workflowTypes";
import WorkflowActivityChangeSetGrid from "./WorkflowActivityChangeSetGrid";
import type { WorkflowActivityChangeSetRow } from "./workflowActivityChangeSetGridProvider";
import { getColumnsFromFieldDefinitions } from "./workflowActivityChangeSetGridProvider";

const maxRowsToShow = 10;

interface Props {
  relatedChangeSet: RelatedChangeSets;
  selectedRelatedChangeSetId?: string;
  onSelectChangeSet?: (changeSet: WorkflowActivityChangeSet) => void;
}

const WorkflowActivityRelatedChangeSetsSection = ({
  relatedChangeSet,
  selectedRelatedChangeSetId,
  onSelectChangeSet,
}: Props) => {
  const [showGridInDialog, setShowGridInDialog] = useState(false);

  const columns = useMemo(
    () => getColumnsFromFieldDefinitions(relatedChangeSet.displayFieldDefinitions, onSelectChangeSet),
    [onSelectChangeSet, relatedChangeSet.displayFieldDefinitions]
  );

  const { rows, hiddenRowCount } = useMemo<{ rows: WorkflowActivityChangeSetRow[]; hiddenRowCount: number }>(() => {
    const allRows = relatedChangeSet.changeSets.map((cs) => ({
      ...cs,
      isSelected: cs.id === selectedRelatedChangeSetId,
    }));

    const rows = allRows.length - maxRowsToShow > 1 ? allRows.slice(0, maxRowsToShow) : allRows;
    const hiddenRowCount = allRows.length - rows.length;
    return { rows, hiddenRowCount };
  }, [relatedChangeSet.changeSets, selectedRelatedChangeSetId]);

  return (
    <>
      <Box sx={{ pt: 1 }}>
        <Typography variant="subtitle2" sx={{ pb: 1 }}>
          {relatedChangeSet.name}
        </Typography>

        <WorkflowActivityChangeSetGrid columns={columns} rows={rows} />

        {hiddenRowCount > 0 && (
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end", alignItems: "center", pt: 1 }}>
            <Typography variant="subtitle2">+ {hiddenRowCount} more rows</Typography>
            <Button variant="text" onClick={() => setShowGridInDialog(true)}>
              Show all
            </Button>
          </Stack>
        )}
      </Box>
      <Dialog fullWidth maxWidth={false} open={showGridInDialog} onClose={() => setShowGridInDialog(false)}>
        <DialogTitle>{relatedChangeSet.name}</DialogTitle>
        <DialogCloseButton onClick={() => setShowGridInDialog(false)} />
        <Divider />
        <DialogContent sx={{ maxHeight: "80vh" }}>
          <WorkflowActivityChangeSetGrid columns={columns} rows={relatedChangeSet.changeSets} />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WorkflowActivityRelatedChangeSetsSection;
